import { useEffect, useMemo, useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import TransactionsPanel from "@/components/support/TransactionsPanel";
import { formatMoney, toAmount, type SupportTransaction } from "@/lib/support";

type AthleteRow = {
  id: string;
  name: string;
  slug: string;
  payment_provider: string;
  payment_url: string | null;
  provider_account_id: string | null;
  support_enabled: boolean;
  connection_id?: string | null;
  token_configured_at?: string | null;
  token_input?: string;
};


type WebhookEvent = {
  id: string;
  provider: string;
  account_hint: string | null;
  status: string;
  note: string | null;
  received_at: string;
  provider_transaction_id: string | null;
};

type Inquiry = {
  id: string;
  athlete_id: string | null;
  company: string;
  contact_name: string;
  email: string;
  phone: string | null;
  opportunity_type: string;
  estimated_budget: string | null;
  timing: string | null;
  description: string | null;
  status: string;
  created_at: string;
};

const AdminSupport = () => {
  const [rows, setRows] = useState<SupportTransaction[]>([]);
  const [athletes, setAthletes] = useState<AthleteRow[]>([]);
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [events, setEvents] = useState<WebhookEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"All" | "Connected" | "Not Connected" | "Disabled">("All");
  const [managingId, setManagingId] = useState<string | null>(null);

  const connectionState = (a: AthleteRow): "Connected" | "Not Connected" | "Disabled" => {
    if (!a.support_enabled) return "Disabled";
    return a.token_configured_at && a.payment_url?.trim() ? "Connected" : "Not Connected";
  };

  const load = async () => {
    setLoading(true);
    const [txRes, athRes, inqRes, evRes, connRes] = await Promise.all([
      supabase.from("support_transactions").select("*").order("transaction_timestamp", { ascending: false }).limit(500),
      supabase.from("athletes").select("id,name,slug,payment_provider,payment_url,provider_account_id,support_enabled").order("name"),
      supabase.from("sponsorship_inquiries").select("*").order("created_at", { ascending: false }).limit(200),
      supabase
        .from("support_webhook_events")
        .select("id,provider,account_hint,status,note,received_at,provider_transaction_id")
        .eq("status", "unmatched")
        .order("received_at", { ascending: false })
        .limit(50),
      // Hashes are server-only; admins see only whether a token is configured.
      supabase.from("payment_connections").select("id,athlete_id,provider,token_configured_at"),
    ]);
    const conns = (connRes.data ?? []) as { id: string; athlete_id: string; provider: string; token_configured_at: string | null }[];
    setRows(((txRes.data ?? []) as unknown) as SupportTransaction[]);
    setAthletes(((athRes.data ?? []) as unknown as AthleteRow[]).map((a) => {
      const c = conns.find((x) => x.athlete_id === a.id && x.provider === (a.payment_provider || "kofi"));
      return { ...a, connection_id: c?.id ?? null, token_configured_at: c?.token_configured_at ?? null, token_input: "" };
    }));
    setInquiries(((inqRes.data ?? []) as unknown) as Inquiry[]);
    setEvents(((evRes.data ?? []) as unknown) as WebhookEvent[]);

    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const nameById = useMemo(() => Object.fromEntries(athletes.map((a) => [a.id, a.name])), [athletes]);

  const filteredAthletes = useMemo(() => {
    const q = query.trim().toLowerCase();
    return athletes.filter((a) => {
      if (q && !a.name.toLowerCase().includes(q) && !a.slug.toLowerCase().includes(q)) return false;
      if (statusFilter !== "All" && connectionState(a) !== statusFilter) return false;
      return true;
    });
  }, [athletes, query, statusFilter]);

  const managing = athletes.find((a) => a.id === managingId) ?? null;

  const byAthlete = useMemo(() => {
    const map = new Map<string, { name: string; total: number; count: number; currency: string }>();
    rows.forEach((r) => {
      const key = r.athlete_id ?? "unassigned";
      const entry = map.get(key) ?? { name: key === "unassigned" ? "Unassigned" : nameById[key] ?? "Unknown athlete", total: 0, count: 0, currency: r.currency };
      entry.total += toAmount(r.amount);
      entry.count += 1;
      map.set(key, entry);
    });
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [rows, nameById]);

  const saveAthlete = async (a: AthleteRow) => {
    const { error } = await supabase
      .from("athletes")
      .update({
        payment_provider: a.payment_provider || "kofi",
        payment_url: a.payment_url?.trim() || null,
        provider_account_id: a.provider_account_id?.trim() || null,
        support_enabled: a.support_enabled,
      })
      .eq("id", a.id);
    if (error) return toast.error(error.message);

    // Keep the explicit webhook mapping in sync — matching relies on it.
    const account = a.provider_account_id?.trim() || null;
    if (account) {
      const { data: existing } = await supabase
        .from("payment_connections")
        .select("id")
        .eq("athlete_id", a.id)
        .eq("provider", a.payment_provider || "kofi")
        .maybeSingle();
      const payload = {
        athlete_id: a.id,
        provider: a.payment_provider || "kofi",
        payment_url: a.payment_url?.trim() || null,
        external_account_identifier: account,
        status: a.support_enabled ? "active" : "disabled",
      };
      const res = existing
        ? await supabase.from("payment_connections").update(payload).eq("id", existing.id)
        : await supabase.from("payment_connections").insert(payload);
      if (res.error) return toast.error(res.error.message);
    }

    // The verification token is sent to a server-only function that stores a
    // hash. It is never written to a client-readable table.
    const token = a.token_input?.trim();
    if (token) {
      let connectionId = a.connection_id;
      if (!connectionId) {
        const { data: c } = await supabase
          .from("payment_connections")
          .select("id")
          .eq("athlete_id", a.id)
          .eq("provider", a.payment_provider || "kofi")
          .maybeSingle();
        connectionId = c?.id ?? null;
      }
      if (!connectionId) return toast.error("Add a provider account first, then save the verification token.");
      const { data, error: fnError } = await supabase.functions.invoke("payment-connection-token", {
        body: { connection_id: connectionId, verification_token: token, provider: a.payment_provider || "kofi" },
      });
      if (fnError || (data as { error?: string })?.error) {
        return toast.error((data as { error?: string })?.error ?? fnError!.message);
      }
      patch(a.id, { token_input: "", token_configured_at: new Date().toISOString() });
    }

    toast.success(`Saved support settings for ${a.name}`);
  };


  const setInquiryStatus = async (id: string, status: string) => {
    const { error } = await supabase.from("sponsorship_inquiries").update({ status }).eq("id", id);
    if (error) return toast.error(error.message);
    setInquiries((list) => list.map((i) => (i.id === id ? { ...i, status } : i)));
  };

  const patch = (id: string, changes: Partial<AthleteRow>) =>
    setAthletes((list) => list.map((a) => (a.id === id ? { ...a, ...changes } : a)));

  const input = "w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary";

  return (
    <AdminLayout>
      <div className="space-y-8">
        <div>
          <h1 className="font-display text-3xl">Athlete Support</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Platform-wide support activity. Payout credentials live with the payment provider — Say It In Sports never stores athlete bank details.
          </p>
        </div>

        <TransactionsPanel rows={rows} loading={loading} csvFilename="siis-support-transactions.csv" showEmail />

        <section className="bg-card rounded-2xl shadow-card p-5">
          <p className="font-display text-xl">Support by athlete</p>
          {byAthlete.length === 0 ? (
            <p className="text-sm text-muted-foreground mt-2">No support recorded yet.</p>
          ) : (
            <ul className="mt-3 divide-y divide-border">
              {byAthlete.map((a) => (
                <li key={a.name} className="flex items-center justify-between py-3 gap-4">
                  <span className="font-semibold text-sm">{a.name}</span>
                  <span className="text-sm text-muted-foreground">{a.count} transactions</span>
                  <span className="font-display text-lg">{formatMoney(a.total, a.currency)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="bg-card rounded-2xl shadow-card p-5">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="font-display text-xl">Support settings</p>
              <p className="text-xs text-muted-foreground mt-1 max-w-3xl">
                The payment URL is provider-agnostic — swap providers here without changing the athlete profile. Incoming
                webhooks are matched to an athlete solely by the hash of that account's verification token; without a saved
                token, events are quarantined and never assigned to an athlete. Tokens are hashed server-side and never stored
                or displayed in plain text.
              </p>
            </div>
            <input
              className={`${input} md:w-64`}
              value={query}
              placeholder="Search athletes…"
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {(["All", "Connected", "Not Connected", "Disabled"] as const).map((f) => (
              <button
                key={f}
                onClick={() => setStatusFilter(f)}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  statusFilter === f
                    ? "bg-secondary text-secondary-foreground border-secondary"
                    : "bg-background text-foreground border-border hover:border-secondary"
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="py-2 pr-4">Athlete</th>
                  <th className="py-2 pr-4">Provider</th>
                  <th className="py-2 pr-4">Connection</th>
                  <th className="py-2 pr-4">Payment URL</th>
                  <th className="py-2 pr-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredAthletes.map((a) => {
                  const s = connectionState(a);
                  return (
                    <tr key={a.id}>
                      <td className="py-3 pr-4 font-semibold">{a.name}</td>
                      <td className="py-3 pr-4 capitalize text-muted-foreground">{a.payment_provider || "kofi"}</td>
                      <td className="py-3 pr-4">
                        <span
                          className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${
                            s === "Connected"
                              ? "bg-primary/15 text-primary"
                              : s === "Disabled"
                                ? "bg-muted text-muted-foreground"
                                : "bg-destructive/10 text-destructive"
                          }`}
                        >
                          {s}
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-muted-foreground">{a.payment_url?.trim() ? "Set" : "Missing"}</td>
                      <td className="py-3 pr-4 text-right">
                        <button
                          onClick={() => setManagingId(a.id)}
                          className="px-4 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-semibold hover:bg-secondary transition-colors"
                        >
                          Manage
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {filteredAthletes.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-6 text-center text-muted-foreground">No athletes match this view.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {managing && (
          <div className="fixed inset-0 z-50 bg-foreground/50 flex items-center justify-center p-4" onClick={() => setManagingId(null)}>
            <div className="bg-card rounded-2xl shadow-bold w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-display text-2xl">{managing.name}</p>
                  <p className="text-xs text-muted-foreground mt-1">Payment settings · {managing.payment_provider || "kofi"}</p>
                </div>
                <button onClick={() => setManagingId(null)} className="text-sm text-muted-foreground hover:text-foreground">Close</button>
              </div>

              <div className="mt-5 space-y-4">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Payment URL</label>
                  <input className={`${input} mt-1.5`} value={managing.payment_url ?? ""} placeholder="https://…" onChange={(e) => patch(managing.id, { payment_url: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Provider account</label>
                  <input className={`${input} mt-1.5`} value={managing.provider_account_id ?? ""} placeholder="ko-fi page name" onChange={(e) => patch(managing.id, { provider_account_id: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Verification token {managing.token_configured_at ? "· configured" : "· not set"}
                  </label>
                  <input
                    type="password"
                    autoComplete="off"
                    className={`${input} mt-1.5`}
                    value={managing.token_input ?? ""}
                    placeholder={managing.token_configured_at ? "Replace token…" : "Paste provider token"}
                    onChange={(e) => patch(managing.id, { token_input: e.target.value })}
                  />
                </div>
                <label className="flex items-center gap-2 text-sm font-semibold">
                  <input type="checkbox" checked={managing.support_enabled} onChange={(e) => patch(managing.id, { support_enabled: e.target.checked })} />
                  Support enabled
                </label>
              </div>

              <div className="mt-6 flex justify-end gap-2">
                <button onClick={() => setManagingId(null)} className="px-4 py-2 rounded-full border border-border text-sm font-semibold">Cancel</button>
                <button
                  onClick={async () => { await saveAthlete(managing); setManagingId(null); }}
                  className="px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:bg-secondary transition-colors"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}


        <section className="bg-card rounded-2xl shadow-card p-5">
          <p className="font-display text-xl">Unmatched webhook events</p>
          <p className="text-xs text-muted-foreground mt-1">
            Events that could not be confidently mapped to an athlete. No earnings were recorded for them. Add the provider
            account above, then re-send the event from the provider.
          </p>
          {events.length === 0 ? (
            <p className="text-sm text-muted-foreground mt-3">Nothing waiting for review.</p>
          ) : (
            <ul className="mt-3 divide-y divide-border">
              {events.map((e) => (
                <li key={e.id} className="py-3 text-sm">
                  <p className="font-semibold">{e.provider} · account: {e.account_hint ?? "unknown"}</p>
                  <p className="text-muted-foreground text-xs mt-0.5">
                    {new Date(e.received_at).toLocaleString()}{e.note ? ` — ${e.note}` : ""}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="bg-card rounded-2xl shadow-card p-5">
          <p className="font-display text-xl">Sponsorship inquiries</p>
          {inquiries.length === 0 ? (
            <p className="text-sm text-muted-foreground mt-2">No inquiries yet.</p>
          ) : (
            <ul className="mt-3 space-y-3">
              {inquiries.map((i) => (
                <li key={i.id} className="rounded-xl border border-border p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-semibold">{i.company} — {i.opportunity_type}</p>
                    <select
                      value={i.status}
                      onChange={(e) => setInquiryStatus(i.id, e.target.value)}
                      className="rounded-full border border-border bg-background px-3 py-1 text-xs font-semibold"
                    >
                      {["new", "in_review", "contacted", "won", "closed"].map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {i.contact_name} · {i.email}{i.phone ? ` · ${i.phone}` : ""} · Athlete: {i.athlete_id ? nameById[i.athlete_id] ?? "—" : "—"}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Budget: {i.estimated_budget ?? "—"} · Timing: {i.timing ?? "—"} · {new Date(i.created_at).toLocaleDateString()}
                  </p>
                  {i.description && <p className="text-sm mt-2 whitespace-pre-wrap">{i.description}</p>}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </AdminLayout>
  );
};

export default AdminSupport;
